import React, { useMemo } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Toolbar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  DashboardNavIcon,
  ComplianceNavIcon,
  ReviewNavIcon,
  LookupNavIcon,
  ServiceProvidersNavIcon,
  ServiceProviderUsersNavIcon,
  InternalUsersNavIcon,
  ProfileNavIcon,
  SettingsNavIcon,
} from './navIcons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/store';
import { setSidebarOpen } from '@/store/slices/uiSlice';
import { routes } from '@/router';
import {
  canAccessComplianceStatusLookup,
  canViewServiceProviders,
  hasAnyRole,
  isAdmin,
  isFieldUser,
  UserRole,
} from '@/utils/roleHelpers';
import { useComplianceReviewAccess } from '@/hooks/useComplianceReviewAccess';

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COLLAPSED = 64;

const NavItemIcon: React.FC<{ sidebarOpen: boolean; children: React.ReactNode }> = ({ sidebarOpen, children }) => (
  <ListItemIcon sx={{ minWidth: 0, mr: sidebarOpen ? 1.75 : 'auto', justifyContent: 'center' }}>
    {children}
  </ListItemIcon>
);

const NavMenuItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  selected: boolean;
  sidebarOpen: boolean;
  onClick: () => void;
}> = ({ icon, label, selected, sidebarOpen, onClick }) => {
  const theme = useTheme();
  return (
    <ListItem disablePadding>
      <ListItemButton
        selected={selected}
        onClick={onClick}
        sx={{
          minHeight: 44,
          justifyContent: sidebarOpen ? 'initial' : 'center',
          px: 2.5,
          position: 'relative',
          color: alpha(theme.palette.text.primary, 0.72),
          '& .MuiListItemIcon-root': { color: alpha(theme.palette.text.primary, 0.6) },
          '& .MuiSvgIcon-root': { fontSize: 22 },
          '& .MuiListItemText-primary': { fontSize: 14, fontWeight: 500 },
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            color: theme.palette.text.primary,
            '& .MuiListItemIcon-root': { color: alpha(theme.palette.text.primary, 0.75) },
          },
          // Selected: a light brand tint, a bar on the left edge, and brand-colored icon and label.
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.primary.main,
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 6,
              bottom: 6,
              width: 3,
              backgroundColor: theme.palette.primary.main,
            },
            '& .MuiListItemIcon-root': { color: theme.palette.primary.main },
            '& .MuiListItemText-primary': { fontWeight: 600 },
            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.14) },
          },
        }}
      >
        <NavItemIcon sidebarOpen={sidebarOpen}>{icon}</NavItemIcon>
        <ListItemText primary={label} sx={{ opacity: sidebarOpen ? 1 : 0 }} />
      </ListItemButton>
    </ListItem>
  );
};

const AppSidebar: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('navigation');

  const { sidebarOpen } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.auth);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { canAccessComplianceReview } = useComplianceReviewAccess();

  const {
    userIsAdmin,
    userCanViewServiceProviders,
    userIsFieldUser,
    userIsSpUser,
    showComplianceStatusLookup,
  } = useMemo(() => {
    const roles = user?.roles ?? [];
    const organization = user?.organizationName ?? "";
    return {
      userIsAdmin: isAdmin(roles),
      userCanViewServiceProviders: canViewServiceProviders(roles, organization),
      userIsFieldUser: isFieldUser(roles),
      // SpAdmin + SpStaff are the SP-side staff who own their own SP's users.
      // Used for menu-item visibility on SP-users pages and mirrors
      // AdminOrSpStaffRoute.
      userIsSpUser: hasAnyRole(roles, [UserRole.SpAdmin, UserRole.SpStaff]),
      // Shared with the compliance-status-lookup/:tab route guard
      // (canAccessComplianceStatusLookup) so nav visibility and route
      // access can't drift apart again.
      showComplianceStatusLookup: canAccessComplianceStatusLookup(roles, organization),
    };
  }, [user]);

  const handleDrawerClose = () => {
    if (isMobile) {
      dispatch(setSidebarOpen(false));
    }
  };

  const handleItemClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      dispatch(setSidebarOpen(false));
    }
  };

  const allMenuItems = useMemo(() => [
    {
      key: 'dashboard',
      label: t('dashboard'),
      icon: <DashboardNavIcon />,
      path: routes.dashboard.path,
      requiresAdmin: false,
    },
    {
      key: 'serviceProviders',
      label: t('serviceProviders'),
      icon: <ServiceProvidersNavIcon />,
      path: routes.serviceProviders.path,
      // Admin (full CRUD) or User (read-only) in a Service Provider-scoped
      // org — not Global Admin-only.
      requiresServiceProviderAccess: true,
    },
    {
      key: 'serviceProviderUsers',
      label: t('serviceProviderUsers'),
      icon: <ServiceProviderUsersNavIcon />,
      path: routes.serviceProviderUsers.path,
      requiresAdmin: true, // Admin only
      allowSpUser: true, // Also allow SpAdmin / SpStaff — mirrors AdminOrSpStaffRoute
    },
    {
      key: 'internalUsers',
      label: t('internalUsers'),
      icon: <InternalUsersNavIcon />,
      path: routes.internalUsers.path,
      requiresAdmin: true,
    },
    {
      key: 'profile',
      label: t('profile'),
      icon: <ProfileNavIcon />,
      path: routes.profile.path,
      requiresAdmin: false,
    },
    {
      key: 'settings',
      label: t('settings'),
      icon: <SettingsNavIcon />,
      path: routes.settings.path,
      requiresAdmin: false,
    },
  ], [t]);

  // Filter menu items based on user role
  const menuItems = useMemo(() => {
    return allMenuItems.filter((item: any) => {
      if (item.requiresServiceProviderAccess) {
        return userCanViewServiceProviders;
      }
      if (item.requiresAdmin) {
        // Admin-only items may opt-in via allowSpUser to also render for
        // SP-side staff (SpAdmin / SpStaff) — same audience as
        // AdminOrSpStaffRoute so nav + guard stay aligned. Admins who can
        // already reach Service Provider Users via the SP detail page
        // (canViewServiceProviders — the 5 CRUD orgs plus the SMT read-only
        // org) are excluded here so the duplicate entry point goes away for
        // them specifically, without touching SpAdmin/SpStaff or admins in
        // any other org.
        if (item.allowSpUser) {
          return userIsSpUser || (userIsAdmin && !userCanViewServiceProviders);
        }
        return userIsAdmin;
      }
      return true;
    });
  }, [allMenuItems, userIsAdmin, userCanViewServiceProviders, userIsSpUser]);

  const drawer = (
    <>
      <Toolbar /> {/* Spacer for AppBar */}
      <List>
        {menuItems.slice(0, 1).map((item) => (
          <NavMenuItem
            key={item.key}
            icon={item.icon}
            label={item.label}
            selected={location.pathname === item.path || location.pathname.startsWith(item.path + '/')}
            sidebarOpen={sidebarOpen}
            onClick={() => handleItemClick(item.path)}
          />
        ))}

        {/* Compliance menu — Drivers, Monitors, and SP-side staff (SpStaff /
            SpAdmin) see Compliance Management; global-side admins see
            Compliance Requirements. SpAdmin used to fall through to the wrong
            branch because isFieldUser only recognized Driver/Monitor +
            env-configured provider-submitter roles (default 'SpStaff,SpAdmin',
            via VITE_PROVIDER_SUBMITTER_ROLES); widen with userIsSpUser so
            SpAdmin gets the same UX as SpStaff even if that default narrows. */}
        {(userIsFieldUser || userIsSpUser) ? (
          // The dashboard cards open the requirements view, so no sub-items here.
          <NavMenuItem
            icon={<ComplianceNavIcon />}
            label={t('complianceManagement')}
            selected={location.pathname.startsWith('/compliance-management')}
            sidebarOpen={sidebarOpen}
            onClick={() => handleItemClick('/compliance-management')}
          />
        ) : (
          // EverDriven, States and Districts are tabs on the page, so no sub-items here.
          <NavMenuItem
            icon={<ComplianceNavIcon />}
            label={t('complianceRequirements')}
            selected={location.pathname.startsWith('/compliance-requirements')}
            sidebarOpen={sidebarOpen}
            onClick={() => handleItemClick('/compliance-requirements')}
          />
        )}

        {/* Compliance Review - Administrator/User in an approving org or ProductAndEngineering */}
        {canAccessComplianceReview && (
          <NavMenuItem
            icon={<ReviewNavIcon />}
            label={t('complianceReview')}
            selected={location.pathname.startsWith('/compliance-review')}
            sidebarOpen={sidebarOpen}
            onClick={() => handleItemClick('/compliance-review')}
          />
        )}

        {/* Compliance Status Lookup — provider submitter roles + internal SP-visibility users */}
        {/* The page's own tabs (Service Provider / Drivers and Monitors / Fleet Vehicles)
            switch views, so no sub-items here. */}
        {showComplianceStatusLookup && (
          <NavMenuItem
            icon={<LookupNavIcon />}
            label={t('complianceStatusLookup')}
            selected={location.pathname.startsWith('/compliance-status-lookup')}
            sidebarOpen={sidebarOpen}
            onClick={() => handleItemClick('/compliance-status-lookup')}
          />
        )}

        {menuItems.slice(1).map((item) => (
          <NavMenuItem
            key={item.key}
            icon={item.icon}
            label={item.label}
            selected={location.pathname === item.path || location.pathname.startsWith(item.path + '/')}
            sidebarOpen={sidebarOpen}
            onClick={() => handleItemClick(item.path)}
          />
        ))}
      </List>
      <Divider />
    </>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={sidebarOpen}
      onClose={handleDrawerClose}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile
      }}
      sx={{
        width: sidebarOpen ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        '& .MuiDrawer-paper': {
          width: sidebarOpen ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          boxSizing: 'border-box',
        },
      }}
    >
      {drawer}
    </Drawer>
  );
};

export default AppSidebar;
